import { FC } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownOption, DropdownProps } from '../../UI';
import { useIncomeSources } from '../../IncomeSource/queries';
import { contributionFieldValuesFromForm, incomePair, isContributionFieldMissing } from '../contributionPairs';
import { useManualContributionEntered } from '../hooks/useManualContributionEntered';
import { InvestmentFormValues } from '../types';

interface InvestmentIncomeSourceDropdownProps
  extends Pick<DropdownProps, 'dropdownMenuClassName' | 'className' | 'label'> {
  householdID: string | undefined | null;
}

export const InvestmentIncomeSourceDropdown: FC<InvestmentIncomeSourceDropdownProps> = ({
  householdID,
  label,
  ...dropdownProps
}) => {
  const { t: tForm } = useTranslation('form');
  const { incomeSources, incomeSourcesQuery } = useIncomeSources({ householdID });
  const { control, setValue, clearErrors } = useFormContext<InvestmentFormValues>();
  const manualContributionEntered = useManualContributionEntered();
  const {
    field,
    fieldState: { error },
  } = useController<InvestmentFormValues, 'incomeSourceID'>({
    control,
    name: 'incomeSourceID',
    rules: {
      // The income pair is "both or neither": an engaged pair (a percentage entered) needs a
      // source. Blocks the save (the form submits through handleSubmit, which gates on validation)
      // rather than persisting a dangling percent.
      validate: (_incomeSourceID, formValues) =>
        isContributionFieldMissing(incomePair, 'incomeSourceID', contributionFieldValuesFromForm(formValues))
          ? tForm('investment-income-source-required-for-percentage')
          : true,
    },
  });

  const options = incomeSources.map((incomeSource) => (
    <DropdownOption key={incomeSource.id} id={incomeSource.id} value={incomeSource.id} label={incomeSource.name} />
  ));

  return (
    <Dropdown
      {...field}
      {...dropdownProps}
      value={field.value}
      loading={incomeSourcesQuery.isLoading}
      withClearSelection
      // Re-resolve the selected option once the async income sources arrive.
      retriggerSelectionFromValue={incomeSourcesQuery.isSuccess}
      status={manualContributionEntered ? 'disabled' : error ? 'errored' : 'normal'}
      helperText={error?.message}
      onChange={(value) => {
        // While the income sources are still loading, the option matching a
        // linked incomeSourceID isn't present yet, so the Dropdown emits a
        // transient onChange(null). Writing that would clear the field, which
        // react-hook-form's controlled `values` immediately restores from the
        // investment — an oscillation that spins for the whole load window and
        // freezes the page (ELE-850). Ignore the null until options exist; a
        // real user clear happens after load, when isSuccess is true.
        if (value == null && !incomeSourcesQuery.isSuccess) return;
        field.onChange(value ?? null);
        if (value == null) {
          // Unlinking clears the whole income pair (percentage included) plus the derived
          // amount/frequency, which have no meaning without a linked source — a single clear
          // fully blanks the contribution rather than leaving the percentage as a straggler.
          setValue('recurringContributionPercentage', null);
          setValue('recurringContribution.amount', null);
          setValue('recurringContribution.frequency', null);
        }
        // Clear any standing contribution errors as the user edits — they re-check on the next save
        // rather than nagging live (validation is submit-only, see useInvestmentForm).
        clearErrors([
          'incomeSourceID',
          'recurringContributionPercentage',
          'recurringContribution.amount',
          'recurringContribution.frequency',
        ]);
      }}
      label={label ?? tForm('investment-income-source-dropdown-label')}
    >
      {options}
    </Dropdown>
  );
};
